import { signal } from "@preact/signals";
import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { auth, useAuth } from "./db.ts";
import { Button } from "./uiComponents.tsx";
import { logError } from "./analytics.ts";
import { trackSignUpConversion } from "./googleAds.ts";
import { monthlyPaymentPerLine, supportEmail } from "../../constants.ts";
import {
  CheckIcon,
  GreenDotIcon,
  MenuIcon,
  StarIcon,
  XIcon,
  ZapIcon,
} from "./icons.tsx";

const loginEmail = signal("");
const emailSent = signal(false);

const inputStyle = {
  height: 46,
  fontSize: 15,
  padding: "0 14px",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius) 0 0 var(--radius)",
  boxSizing: "border-box",
  outline: "none",
  background: "var(--surface)",
  color: "var(--text)",
  marginBottom: 0,
  flex: 1,
  minWidth: 0,
} as const;

const buttonStyle = {
  height: 46,
  fontSize: 15,
  borderRadius: "0 var(--radius) var(--radius) 0",
  marginLeft: -1,
  padding: "0 18px",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--border)",
  borderLeft: "none",
  background: "var(--emerald)",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  transition: "all 0.15s ease",
  whiteSpace: "nowrap",
  flexShrink: 0,
} as const;

const Email = () => (
  <div style={{ display: "flex", alignItems: "stretch", width: "100%" }}>
    <input
      style={inputStyle}
      placeholder="developer@company.com"
      type="email"
      value={loginEmail.value}
      onInput={({ currentTarget: { value } }) => {
        loginEmail.value = value;
      }}
    />
    <Button
      style={buttonStyle}
      onClick={() => {
        if (!loginEmail.value) {
          toast("Fill in your email!");
          return;
        }
        auth
          .sendMagicCode({ email: loginEmail.value })
          .then(() => {
            emailSent.value = true;
          })
          .catch((err) => {
            toast(err.body?.message);
            logError(err);
            loginEmail.value = "";
          });
      }}
    >
      Start Free Trial
    </Button>
  </div>
);

const MagicCode = () => {
  const [code, setCode] = useState("");
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%",
      }}
    >
      <div
        style={{
          textAlign: "center",
          fontSize: "1em",
          color: "var(--text)",
        }}
      >
        We sent a 6-digit code to <strong>{loginEmail.value}</strong>
      </div>
      <div style={{ display: "flex", alignItems: "stretch", width: "100%" }}>
        <input
          type="number"
          placeholder="123456"
          value={code}
          onInput={(e) => setCode(e.currentTarget.value)}
          style={inputStyle}
        />
        <Button
          onClick={() => {
            auth
              .signInWithMagicCode({ email: loginEmail.value, code })
              .then(() => {
                trackSignUpConversion(loginEmail.value);
                emailSent.value = false;
                toast("Signed in!");
              })
              .catch((err) => {
                toast("Wrong code");
                logError(err);
                setCode("");
              });
          }}
          style={buttonStyle}
        >
          Verify Code
        </Button>
      </div>
      <div
        style={{ fontSize: "0.8em", color: "var(--text-muted)", opacity: 0.8 }}
      >
        Check your spam folder if it does not arrive within a minute.
      </div>
    </div>
  );
};

const navBarStyle = {
  position: "sticky",
  top: 0,
  zIndex: 50,
  width: "100%",
  background: "rgba(14, 18, 23, 0.85)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  borderBottom: "1px solid var(--border-subtle)",
} as const;

const navInnerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  maxWidth: 1160,
  margin: "0 auto",
  padding: "14px 24px",
  boxSizing: "border-box",
} as const;

const navLogoStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  color: "var(--text)",
  textDecoration: "none",
  fontWeight: 800,
  fontSize: "1.2em",
  letterSpacing: "-0.02em",
} as const;

const navLinksStyle = {
  display: "flex",
  alignItems: "center",
  gap: 24,
} as const;

const navLinkItemStyle = {
  color: "var(--text-muted)",
  textDecoration: "none",
  fontSize: 14,
  fontWeight: 500,
  transition: "color 0.15s ease",
} as const;

const navButtonStyle = {
  background: "var(--emerald)",
  borderColor: "var(--emerald)",
  color: "#fff",
  fontWeight: 700,
  padding: "0.45rem 0.9rem",
  fontSize: 13,
} as const;

const mobileNavDrawerStyle = {
  flexDirection: "column",
  gap: 0,
  padding: "10px 20px 16px",
  borderTop: "1px solid var(--border-subtle)",
  background: "var(--surface)",
} as const;

const mobileNavLinkStyle = {
  color: "var(--text-muted)",
  textDecoration: "none",
  fontSize: 15,
  fontWeight: 500,
  padding: "10px 0",
  borderBottom: "1px solid var(--border-subtle)",
  transition: "color 0.15s ease",
} as const;

const heroContainerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  maxWidth: 960,
  margin: "0 auto",
  padding: "72px 20px 48px",
  boxSizing: "border-box",
  position: "relative",
} as const;

const heroBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: "var(--emerald-subtle)",
  color: "var(--emerald)",
  border: "1px solid rgba(34, 197, 94, 0.25)",
  padding: "6px 16px",
  borderRadius: 100,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  marginBottom: 24,
  boxShadow: "0 0 20px var(--emerald-glow)",
  maxWidth: "100%",
  boxSizing: "border-box",
} as const;

const heroTitleStyle = {
  margin: "0 0 20px 0",
  fontSize: "clamp(2rem, 5.5vw, 4.2rem)",
  fontWeight: 800,
  color: "var(--text)",
  letterSpacing: "-0.035em",
  lineHeight: 1.1,
  overflowWrap: "break-word",
  wordBreak: "break-word",
} as const;

const heroGradientSpanStyle = {
  background: "linear-gradient(135deg, #22c55e 0%, #4ade80 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
} as const;

const heroSubtitleStyle = {
  color: "var(--text-muted)",
  fontSize: "clamp(1.05rem, 2vw, 1.25rem)",
  lineHeight: 1.6,
  margin: "0 auto 36px",
  maxWidth: 720,
} as const;

const loginCardStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: "24px 20px",
  border: "1px solid rgba(34, 197, 94, 0.3)",
  borderRadius: 12,
  background: "var(--surface-alt)",
  width: "100%",
  maxWidth: 480,
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.45), 0 0 30px var(--emerald-glow)",
  textAlign: "center",
  boxSizing: "border-box",
} as const;

const heroPillsStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 18,
  flexWrap: "wrap",
  marginTop: 22,
  fontSize: 13,
  color: "var(--text-muted)",
} as const;

const statsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
  gap: 18,
  width: "100%",
  maxWidth: 1100,
  margin: "24px auto 60px",
  padding: "0 20px",
  boxSizing: "border-box",
} as const;

const statCardStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: "22px 24px",
  background: "var(--surface-card)",
  border: "1px solid var(--border)",
  borderRadius: 10,
  textAlign: "left",
} as const;

const statNumberStyle = {
  fontSize: "2.4em",
  fontWeight: 800,
  letterSpacing: "-0.03em",
  color: "var(--text)",
  lineHeight: 1.1,
} as const;

const statLabelStyle = {
  fontSize: "0.85em",
  color: "var(--text-muted)",
  lineHeight: 1.4,
} as const;

const sectionHeaderStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  maxWidth: 720,
  margin: "0 auto 40px",
  padding: "0 20px",
} as const;

const sectionTitleStyle = {
  fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)",
  fontWeight: 800,
  color: "var(--text)",
  letterSpacing: "-0.025em",
  margin: "0 0 12px 0",
  overflowWrap: "break-word",
  wordBreak: "break-word",
} as const;

const sectionSubtitleStyle = {
  fontSize: "1.05em",
  color: "var(--text-muted)",
  lineHeight: 1.6,
  margin: 0,
} as const;

const bentoGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
  gap: 20,
  width: "100%",
  maxWidth: 1100,
  margin: "0 auto",
  padding: "0 20px",
  boxSizing: "border-box",
} as const;

const bentoCardStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: 28,
  background: "var(--surface-card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  textAlign: "left",
  transition: "border-color 0.2s ease, transform 0.2s ease",
} as const;

const bentoCardFeaturedStyle = {
  ...bentoCardStyle,
  border: "1px solid rgba(34, 197, 94, 0.35)",
  background:
    "linear-gradient(180deg, rgba(34, 197, 94, 0.05) 0%, var(--surface-card) 100%)",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.3)",
} as const;

const bentoIconContainerStyle = {
  width: 44,
  height: 44,
  borderRadius: 8,
  background: "var(--emerald-subtle)",
  color: "var(--emerald)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 18,
} as const;

const bentoTagStyle = {
  marginTop: 20,
  fontSize: 12,
  color: "var(--emerald)",
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
} as const;

const showcaseWrapperStyle = {
  width: "100%",
  maxWidth: 1040,
  margin: "0 auto 80px",
  padding: "0 20px",
  boxSizing: "border-box",
} as const;

const codeCardStyle = {
  background: "#080c11",
  border: "1px solid var(--border)",
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "0 20px 50px rgba(0, 0, 0, 0.55)",
} as const;

const codeHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 18px",
  background: "#0e131b",
  borderBottom: "1px solid var(--border-subtle)",
  flexWrap: "wrap",
  gap: 10,
} as const;

const tableWrapperStyle = {
  width: "100%",
  maxWidth: 1060,
  margin: "0 auto 80px",
  padding: "0 20px",
  boxSizing: "border-box",
} as const;

const comparisonTableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.92em",
  textAlign: "left",
  background: "var(--surface-card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  overflow: "hidden",
} as const;

const thStyle = {
  padding: "16px 20px",
  borderBottom: "1px solid var(--border)",
  background: "rgba(0, 0, 0, 0.35)",
  fontWeight: 700,
  color: "var(--text)",
  fontSize: "0.95em",
} as const;

const tdStyle = {
  padding: "14px 20px",
  borderBottom: "1px solid var(--border-subtle)",
  color: "var(--text-muted)",
} as const;

const tdSupergreenStyle = {
  padding: "14px 20px",
  borderBottom: "1px solid var(--border-subtle)",
  color: "var(--emerald)",
  fontWeight: 600,
  background: "var(--emerald-subtle)",
} as const;

const pricingGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
  gap: 24,
  width: "100%",
  maxWidth: 1060,
  margin: "0 auto 80px",
  padding: "0 20px",
  boxSizing: "border-box",
} as const;

const pricingCardStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: 32,
  border: "1px solid var(--border)",
  borderRadius: 12,
  background: "var(--surface-card)",
  textAlign: "left",
} as const;

const pricingCardPopularStyle = {
  ...pricingCardStyle,
  border: "1.5px solid var(--emerald)",
  boxShadow: "0 0 35px var(--emerald-glow)",
  position: "relative",
} as const;

const testimonialsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
  gap: 20,
  width: "100%",
  maxWidth: 1060,
  margin: "0 auto 80px",
  padding: "0 20px",
  boxSizing: "border-box",
} as const;

const testimonialCardStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: 26,
  background: "var(--surface-card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  textAlign: "left",
  gap: 16,
} as const;

const faqWrapperStyle = {
  width: "100%",
  maxWidth: 820,
  margin: "0 auto 90px",
  padding: "0 20px",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: 12,
} as const;

const faqItemStyle = {
  background: "var(--surface-card)",
  border: "1px solid var(--border)",
  borderRadius: 10,
  overflow: "hidden",
  transition: "border-color 0.15s ease",
} as const;

const faqQuestionStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "18px 22px",
  fontSize: "1.05em",
  fontWeight: 700,
  color: "var(--text)",
  cursor: "pointer",
  background: "transparent",
  border: "none",
  width: "100%",
  textAlign: "left",
} as const;

const faqAnswerStyle = {
  padding: "0 22px 20px",
  color: "var(--text-muted)",
  fontSize: "0.95em",
  lineHeight: 1.6,
} as const;

const finalCtaBoxStyle = {
  width: "100%",
  maxWidth: 1000,
  margin: "0 auto 90px",
  padding: "54px 32px",
  boxSizing: "border-box",
  background:
    "radial-gradient(ellipse at top, rgba(34, 197, 94, 0.15), transparent 70%), var(--surface-card)",
  border: "1.5px solid rgba(34, 197, 94, 0.35)",
  borderRadius: 16,
  textAlign: "center",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px var(--emerald-glow)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 24,
} as const;

const footerStyle = {
  width: "100%",
  background: "#080b0f",
  borderTop: "1px solid var(--border-subtle)",
  padding: "50px 24px 30px",
  boxSizing: "border-box",
} as const;

const footerInnerStyle = {
  maxWidth: 1060,
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 32,
  textAlign: "left",
  marginBottom: 40,
} as const;

const footerColTitleStyle = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--text)",
  marginBottom: 14,
} as const;

const footerLinkListStyle = {
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  fontSize: 14,
} as const;

const footerLinkItemStyle = {
  color: "var(--text-muted)",
  textDecoration: "none",
  transition: "color 0.15s ease",
} as const;

const AuthCardContent = ({
  isLoading,
  user,
}: {
  isLoading: boolean;
  user: { email?: string | null } | undefined | null;
}) => {
  if (isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ fontSize: "0.9em", color: "var(--text-muted)" }}>
          Checking session...
        </span>
      </div>
    );
  }

  if (user) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: "1.05em", fontWeight: 700 }}>
          Welcome back, {user.email}
        </div>
        <a
          href="/app"
          className="button primary"
          style={{
            justifyContent: "center",
            height: 46,
            fontSize: 15,
            fontWeight: 700,
            background: "var(--emerald)",
            borderColor: "var(--emerald)",
          }}
        >
          Go to Developer Console
        </a>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {emailSent.value ? <MagicCode /> : <Email />}
      <div style={{ fontSize: "0.8em", color: "var(--text-muted)" }}>
        Instant setup · Free trial · Zero credit card required
      </div>
    </div>
  );
};

export const HeaderNav = ({
  user,
}: {
  user?: { email?: string | null } | null;
} = {}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header style={navBarStyle}>
      <div className="nav-inner" style={navInnerStyle}>
        <a href="/" style={navLogoStyle}>
          <GreenDotIcon size={20} />
          <span>Supergreen</span>
        </a>

        <nav className="desktop-nav" style={navLinksStyle}>
          <a href="#features" style={navLinkItemStyle}>Features</a>
          <a href="#n8n" style={navLinkItemStyle}>n8n Node</a>
          <a href="#comparison" style={navLinkItemStyle}>Comparison</a>
          <a href="#pricing" style={navLinkItemStyle}>Pricing</a>
          <a href="#faq" style={navLinkItemStyle}>FAQ</a>
          <a href="/docs" style={navLinkItemStyle}>API Docs</a>
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <a
            href={user ? "/app" : "#signup"}
            className="button"
            style={navButtonStyle}
          >
            {user ? "Dashboard" : "Sign In"}
          </a>
          <button
            type="button"
            className="mobile-menu-btn"
            aria-label="Toggle navigation menu"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <XIcon size={18} /> : <MenuIcon size={18} />}
          </button>
        </div>
      </div>

      <div
        className={`mobile-nav-drawer ${menuOpen ? "open" : ""}`}
        style={mobileNavDrawerStyle}
      >
        <a
          href="#features"
          style={mobileNavLinkStyle}
          onClick={() => setMenuOpen(false)}
        >
          Features
        </a>
        <a
          href="#n8n"
          style={mobileNavLinkStyle}
          onClick={() => setMenuOpen(false)}
        >
          n8n Node
        </a>
        <a
          href="#comparison"
          style={mobileNavLinkStyle}
          onClick={() => setMenuOpen(false)}
        >
          Comparison
        </a>
        <a
          href="#pricing"
          style={mobileNavLinkStyle}
          onClick={() => setMenuOpen(false)}
        >
          Pricing
        </a>
        <a
          href="#faq"
          style={mobileNavLinkStyle}
          onClick={() => setMenuOpen(false)}
        >
          FAQ
        </a>
        <a
          href="/docs"
          style={{ ...mobileNavLinkStyle, borderBottom: "none" }}
          onClick={() => setMenuOpen(false)}
        >
          API Docs
        </a>
      </div>
    </header>
  );
};

export const InteractiveShowcase = () => {
  const [tab, setTab] = useState<"n8n" | "curl" | "webhook">("n8n");

  const tabButtonStyle = (isActive: boolean) => ({
    background: isActive ? "var(--surface-alt)" : "transparent",
    color: isActive ? "#fff" : "var(--text-muted)",
    border: "none",
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  });

  return (
    <div id="n8n" style={showcaseWrapperStyle}>
      <div style={sectionHeaderStyle}>
        <div style={heroBadgeStyle}>Developer First Architecture</div>
        <h2 style={sectionTitleStyle}>Built for n8n & Automated Systems</h2>
        <p style={sectionSubtitleStyle}>
          Verified on n8n Cloud — add the Supergreen node directly from the
          canvas, or install into self-hosted instances with one click.
        </p>
      </div>

      <div style={codeCardStyle}>
        <div style={codeHeaderStyle}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              minWidth: 0,
              flexShrink: 1,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.15)",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.15)",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.15)",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                marginLeft: 12,
                fontSize: 12,
                color: "var(--text-muted)",
                fontFamily: "var(--font-mono)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "calc(100vw - 140px)",
              }}
            >
              {tab === "n8n"
                ? "n8n-nodes-supergreen (Community Node)"
                : tab === "curl"
                ? "POST https://api.supergreen.cc/api"
                : "Webhook Payload Dispatch"}
            </span>
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setTab("n8n")}
              style={tabButtonStyle(tab === "n8n")}
            >
              n8n Node
            </button>
            <button
              type="button"
              onClick={() => setTab("curl")}
              style={tabButtonStyle(tab === "curl")}
            >
              cURL API
            </button>
            <button
              type="button"
              onClick={() => setTab("webhook")}
              style={tabButtonStyle(tab === "webhook")}
            >
              Inbound Webhook
            </button>
          </div>
        </div>

        <div style={{ padding: "24px 28px", overflowX: "auto" }}>
          {tab === "n8n" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div>
                  <div
                    style={{
                      color: "var(--emerald)",
                      fontWeight: 700,
                      fontSize: "1.1em",
                    }}
                  >
                    Verified on n8n Cloud & Community Registry
                  </div>
                  <div
                    style={{
                      color: "var(--text-muted)",
                      fontSize: 13,
                      marginTop: 4,
                    }}
                  >
                    <strong>n8n Cloud:</strong> Search <code>Supergreen</code>
                    {" "}
                    directly on canvas (+)
                    <br />
                    <strong>Self-hosted:</strong>{" "}
                    Settings → Community Nodes → Install
                  </div>
                </div>
                <code
                  style={{
                    background: "#161d27",
                    padding: "8px 14px",
                    borderRadius: 6,
                    border: "1px solid var(--border)",
                    color: "#f5f7fa",
                    fontFamily: "var(--font-mono)",
                    fontSize: 13,
                  }}
                >
                  n8n-nodes-supergreen
                </code>
              </div>

              <pre
                style={{
                  margin: 0,
                  color: "#94a3b8",
                  fontFamily: "var(--font-mono)",
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {`// Supported Operations in n8n Node:
• Send WhatsApp Text (with URL previews & mentions)
• Send Media (PDF invoices, images, videos, audio notes)
• Send Interactive Polls & Reactions
• Full Group Chat Support (read inbound & dispatch to groups)
• Real-time Webhook Receiver node for two-way chat flows`}
              </pre>

              <div style={{ display: "flex", gap: 14, marginTop: 4 }}>
                <a
                  href="https://www.npmjs.com/package/n8n-nodes-supergreen"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "var(--primary)",
                    fontSize: 13,
                    textDecoration: "underline",
                  }}
                >
                  View on npm
                </a>
                <a
                  href="https://github.com/uriva/n8n-nodes-supergreen"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "var(--text-muted)",
                    fontSize: 13,
                    textDecoration: "underline",
                  }}
                >
                  View GitHub Source
                </a>
              </div>
            </div>
          )}

          {tab === "curl" && (
            <pre
              style={{
                margin: 0,
                color: "#94a3b8",
                fontFamily: "var(--font-mono)",
                fontSize: 13,
                lineHeight: 1.6,
                textAlign: "left",
              }}
            >
              {`curl -X POST https://api.supergreen.cc/api \\
  -H "Content-Type: application/json" \\
  -d '{
    "endpoint": "sendMessage",
    "payload": {
      "number": "15551234567",
      "token": "your_account_secret_token",
      "to": "120363025492819283@g.us",
      "message": "Order #4892 verified. Invoice attached.",
      "fileUrl": "https://company.com/invoices/inv_4892.pdf",
      "fileName": "Invoice-4892.pdf"
    }
  }'`}
            </pre>
          )}

          {tab === "webhook" && (
            <pre
              style={{
                margin: 0,
                color: "#94a3b8",
                fontFamily: "var(--font-mono)",
                fontSize: 13,
                lineHeight: 1.6,
                textAlign: "left",
              }}
            >
              {`// Real-time POST payload sent to your webhook URL
{
  "event": "onMessage",
  "from": "15559876543@c.us",
  "chatId": "120363025492819283@g.us",
  "isGroup": true,
  "author": "15559876543",
  "body": "Can someone update ticket #902?",
  "timestamp": 1757422910,
  "senderName": "Maya Lin",
  "hasMedia": false
}`}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

export const BentoFeatures = () => (
  <section id="features" style={{ width: "100%", marginBottom: 90 }}>
    <div style={sectionHeaderStyle}>
      <div style={heroBadgeStyle}>Why Supergreen Works</div>
      <h2 style={sectionTitleStyle}>
        Infrastructure Built to Eliminate Bans
      </h2>
      <p style={sectionSubtitleStyle}>
        Other self-hosted libraries crash or trigger soft-blocks because of IP
        churn. Supergreen provides dedicated Linux VMs, static residential proxy
        pinning, and automated health recovery.
      </p>
    </div>

    <div style={bentoGridStyle}>
      {/* 1. Static Proxy Pinning */}
      <div style={bentoCardFeaturedStyle}>
        <div>
          <div style={bentoIconContainerStyle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
              <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
              <line x1="6" y1="6" x2="6.01" y2="6" />
              <line x1="6" y1="18" x2="6.01" y2="18" />
            </svg>
          </div>
          <h3
            style={{
              fontSize: "1.3em",
              fontWeight: 700,
              color: "var(--text)",
              margin: "0 0 10px",
            }}
          >
            Static Residential Proxy Pinning
          </h3>
          <p
            style={{
              fontSize: "0.95em",
              color: "var(--text-muted)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            WhatsApp monitors connection IP stability. Rotating proxies trigger
            soft-blocks within hours. Supergreen pins every account to a
            dedicated, consistent static residential proxy to maintain healthy
            session trust.
          </p>
        </div>
        <div style={bentoTagStyle}>
          <CheckIcon size={14} color="var(--emerald)" strokeWidth={2.5} />
          <span>Prevents soft-blocks & TOS_BLOCK loops</span>
        </div>
      </div>

      {/* 2. Dedicated Docker Worker */}
      <div style={bentoCardStyle}>
        <div>
          <div style={bentoIconContainerStyle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
          </div>
          <h3
            style={{
              fontSize: "1.3em",
              fontWeight: 700,
              color: "var(--text)",
              margin: "0 0 10px",
            }}
          >
            Isolated Docker VM Containers
          </h3>
          <p
            style={{
              fontSize: "0.95em",
              color: "var(--text-muted)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Every line runs in its own headless Chrome container on high-speed
            Hetzner nodes. Session credentials, browser state, and auth tokens
            are isolated from other tenants with automated restart watchdogs.
          </p>
        </div>
        <div style={bentoTagStyle}>
          <CheckIcon size={14} color="var(--emerald)" strokeWidth={2.5} />
          <span>Dedicated CPU, memory & storage volume</span>
        </div>
      </div>

      {/* 3. Group Chat Support */}
      <div style={bentoCardStyle}>
        <div>
          <div style={bentoIconContainerStyle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h3
            style={{
              fontSize: "1.3em",
              fontWeight: 700,
              color: "var(--text)",
              margin: "0 0 10px",
            }}
          >
            Full WhatsApp Group Chat Support
          </h3>
          <p
            style={{
              fontSize: "0.95em",
              color: "var(--text-muted)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Meta Cloud API explicitly blocks bots from reading or sending to
            WhatsApp groups. Supergreen gives you full read and write access to
            group chats, participant mentions, and admin alerts.
          </p>
        </div>
        <div style={bentoTagStyle}>
          <CheckIcon size={14} color="var(--emerald)" strokeWidth={2.5} />
          <span>Read group messages, quote, and dispatch</span>
        </div>
      </div>

      {/* 4. Rich Media & PDFs */}
      <div style={bentoCardStyle}>
        <div>
          <div style={bentoIconContainerStyle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <h3
            style={{
              fontSize: "1.3em",
              fontWeight: 700,
              color: "var(--text)",
              margin: "0 0 10px",
            }}
          >
            Zero Template Approvals
          </h3>
          <p
            style={{
              fontSize: "0.95em",
              color: "var(--text-muted)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Send free-form text, generated PDF invoices, audio messages, images,
            and interactive polls instantly. You never have to submit templates
            to Meta for review or wait 48 hours for marketing approvals.
          </p>
        </div>
        <div style={bentoTagStyle}>
          <CheckIcon size={14} color="var(--emerald)" strokeWidth={2.5} />
          <span>PDFs, images, voice notes & polls</span>
        </div>
      </div>

      {/* 5. Telegram MTProto Daemon */}
      <div style={bentoCardStyle}>
        <div>
          <div style={bentoIconContainerStyle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </div>
          <h3
            style={{
              fontSize: "1.3em",
              fontWeight: 700,
              color: "var(--text)",
              margin: "0 0 10px",
            }}
          >
            Direct Telegram MTProto Daemon
          </h3>
          <p
            style={{
              fontSize: "0.95em",
              color: "var(--text-muted)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Lightweight, high-concurrency Telegram daemon communicating directly
            over Telegram's native binary MTProto protocol. No headless Chrome
            required, zero memory bloat, instant message delivery.
          </p>
        </div>
        <div style={bentoTagStyle}>
          <CheckIcon size={14} color="var(--emerald)" strokeWidth={2.5} />
          <span>Multi-account channels & private chats</span>
        </div>
      </div>

      {/* 6. Multi-Platform Extras */}
      <div style={bentoCardStyle}>
        <div>
          <div style={bentoIconContainerStyle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <h3
            style={{
              fontSize: "1.3em",
              fontWeight: 700,
              color: "var(--text)",
              margin: "0 0 10px",
            }}
          >
            LinkedIn, FB & Virtual DIDs
          </h3>
          <p
            style={{
              fontSize: "0.95em",
              color: "var(--text-muted)",
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Need more than messaging? Use your slots for Facebook group scraping
            via remote browser, LinkedIn network outreach, or purchase virtual
            SMS phone numbers (DIDs) with webhook forwarding.
          </p>
        </div>
        <div style={bentoTagStyle}>
          <CheckIcon size={14} color="var(--emerald)" strokeWidth={2.5} />
          <span>Unified communication control plane</span>
        </div>
      </div>
    </div>
  </section>
);

export const ComparisonSection = () => (
  <section id="comparison" style={tableWrapperStyle}>
    <div style={sectionHeaderStyle}>
      <div style={heroBadgeStyle}>Clear Advantage</div>
      <h2 style={sectionTitleStyle}>Supergreen vs The Alternatives</h2>
      <p style={sectionSubtitleStyle}>
        Why teams switch from Meta Cloud API fees and brittle self-hosted
        containers to Supergreen.
      </p>
    </div>

    <div style={{ overflowX: "auto" }}>
      <table style={comparisonTableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Feature</th>
            <th style={{ ...thStyle, color: "var(--emerald)" }}>Supergreen</th>
            <th style={thStyle}>Meta Cloud API</th>
            <th style={thStyle}>Self-Hosted Baileys</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>Setup Time</td>
            <td style={tdSupergreenStyle}>60 seconds (Scan QR code)</td>
            <td style={tdStyle}>Days (Meta Business verification)</td>
            <td style={tdStyle}>Hours (Server & docker setup)</td>
          </tr>
          <tr>
            <td style={tdStyle}>Cost per Message</td>
            <td style={tdSupergreenStyle}>$0.00 (Flat $10/month slot)</td>
            <td style={tdStyle}>$0.03 to $0.08+ per conversation</td>
            <td style={tdStyle}>Server hosting + maintenance</td>
          </tr>
          <tr>
            <td style={tdStyle}>Message Templates</td>
            <td style={tdSupergreenStyle}>
              Unrestricted free-form text & media
            </td>
            <td style={tdStyle}>Pre-approved templates only</td>
            <td style={tdStyle}>Unrestricted</td>
          </tr>
          <tr>
            <td style={tdStyle}>WhatsApp Groups</td>
            <td style={tdSupergreenStyle}>Full read & write support</td>
            <td style={tdStyle}>Not allowed for bots</td>
            <td style={tdStyle}>Supported</td>
          </tr>
          <tr>
            <td style={tdStyle}>Ban Protection</td>
            <td style={tdSupergreenStyle}>
              Dedicated static residential proxy pinning
            </td>
            <td style={tdStyle}>Official channel (safe)</td>
            <td style={tdStyle}>Frequent bans from server IP churn</td>
          </tr>
          <tr>
            <td style={tdStyle}>n8n Integration</td>
            <td style={tdSupergreenStyle}>
              Official 1-click community node
            </td>
            <td style={tdStyle}>Official node (requires Meta app)</td>
            <td style={tdStyle}>Manual webhook script wiring</td>
          </tr>
          <tr>
            <td style={{ ...tdStyle, borderBottom: "none" }}>
              Multi-Client Scaling
            </td>
            <td style={{ ...tdSupergreenStyle, borderBottom: "none" }}>
              Unified dashboard & isolated tokens
            </td>
            <td style={{ ...tdStyle, borderBottom: "none" }}>
              Complex WABA accounts per client
            </td>
            <td style={{ ...tdStyle, borderBottom: "none" }}>
              Manual DevOps per container
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
);

const PricingCheckItem = (
  { children }: { children: preact.ComponentChildren },
) => (
  <li style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <CheckIcon size={14} color="var(--emerald)" strokeWidth={2.5} />
    <span>{children}</span>
  </li>
);

const StarRating = () => (
  <div style={{ display: "flex", gap: 3, color: "var(--emerald)" }}>
    <StarIcon size={16} />
    <StarIcon size={16} />
    <StarIcon size={16} />
    <StarIcon size={16} />
    <StarIcon size={16} />
  </div>
);

export const AgencyPricingSection = () => (
  <section id="pricing" style={{ width: "100%", marginBottom: 90 }}>
    <div style={sectionHeaderStyle}>
      <div style={heroBadgeStyle}>Transparent Flat Pricing</div>
      <h2 style={sectionTitleStyle}>Simple Plans for Builders & Agencies</h2>
      <p style={sectionSubtitleStyle}>
        No usage meters, no surprise per-conversation charges. Every slot
        includes a dedicated static proxy and full API access.
      </p>
    </div>

    <div style={pricingGridStyle}>
      {/* Solo Plan */}
      <div style={pricingCardStyle}>
        <div>
          <div style={{ fontSize: "1.25em", fontWeight: 700 }}>Solo Slot</div>
          <div style={{ fontSize: "2.6em", fontWeight: 800, margin: "10px 0" }}>
            ${monthlyPaymentPerLine}
            <span
              style={{
                fontSize: "0.45em",
                color: "var(--text-muted)",
                fontWeight: 400,
              }}
            >
              {" "}
              / month
            </span>
          </div>
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: "0.95em",
              margin: "0 0 20px",
            }}
          >
            Perfect for indie hackers and businesses with a single primary line.
          </p>

          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              fontSize: "0.92em",
            }}
          >
            <PricingCheckItem>1 flexible connected slot</PricingCheckItem>
            <PricingCheckItem>
              WhatsApp, Telegram, FB or LinkedIn
            </PricingCheckItem>
            <PricingCheckItem>
              Static residential proxy allocation
            </PricingCheckItem>
            <PricingCheckItem>
              Native n8n community node access
            </PricingCheckItem>
            <PricingCheckItem>Real-time webhooks & REST API</PricingCheckItem>
            <PricingCheckItem>Free trial included</PricingCheckItem>
          </ul>
        </div>

        <a
          href="#signup"
          className="button"
          style={{
            marginTop: 28,
            justifyContent: "center",
            background: "var(--surface)",
            borderColor: "var(--border)",
            fontWeight: 700,
          }}
        >
          Start Solo Trial
        </a>
      </div>

      {/* Agency 5-Pack */}
      <div style={pricingCardPopularStyle}>
        <div
          style={{
            position: "absolute",
            top: -12,
            right: 24,
            background: "var(--emerald)",
            color: "#fff",
            fontSize: 11,
            fontWeight: 800,
            padding: "4px 12px",
            borderRadius: 100,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Most Popular
        </div>

        <div>
          <div
            style={{
              fontSize: "1.25em",
              fontWeight: 700,
              color: "var(--emerald)",
            }}
          >
            Agency 5-Pack
          </div>
          <div style={{ fontSize: "2.6em", fontWeight: 800, margin: "10px 0" }}>
            $45
            <span
              style={{
                fontSize: "0.45em",
                color: "var(--text-muted)",
                fontWeight: 400,
              }}
            >
              {" "}
              / month ($9/line)
            </span>
          </div>
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: "0.95em",
              margin: "0 0 20px",
            }}
          >
            Built for automation consultants and agencies running client bots.
          </p>

          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              fontSize: "0.92em",
            }}
          >
            <PricingCheckItem>5 service slots included</PricingCheckItem>
            <PricingCheckItem>Multi-tenant routing per client</PricingCheckItem>
            <PricingCheckItem>Independent webhooks & tokens</PricingCheckItem>
            <PricingCheckItem>
              Dedicated proxy per connected line
            </PricingCheckItem>
            <PricingCheckItem>High-priority email support</PricingCheckItem>
            <PricingCheckItem>Automated reconnect watchdogs</PricingCheckItem>
          </ul>
        </div>

        <a
          href="#signup"
          className="button"
          style={{
            marginTop: 28,
            justifyContent: "center",
            background: "var(--emerald)",
            borderColor: "var(--emerald)",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          Get 5-Pack
        </a>
      </div>

      {/* Agency Scale */}
      <div style={pricingCardStyle}>
        <div>
          <div style={{ fontSize: "1.25em", fontWeight: 700 }}>
            Agency Scale
          </div>
          <div style={{ fontSize: "2.6em", fontWeight: 800, margin: "10px 0" }}>
            $140
            <span
              style={{
                fontSize: "0.45em",
                color: "var(--text-muted)",
                fontWeight: 400,
              }}
            >
              {" "}
              / month ($7/line)
            </span>
          </div>
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: "0.95em",
              margin: "0 0 20px",
            }}
          >
            For high-volume lead pipelines and scaling agency operations.
          </p>

          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              fontSize: "0.92em",
            }}
          >
            <PricingCheckItem>20 service slots included</PricingCheckItem>
            <PricingCheckItem>High-concurrency message queues</PricingCheckItem>
            <PricingCheckItem>Custom webhook latency tuning</PricingCheckItem>
            <PricingCheckItem>Global proxy IP allocations</PricingCheckItem>
            <PricingCheckItem>Direct support: {supportEmail}</PricingCheckItem>
            <PricingCheckItem>Custom SLA available</PricingCheckItem>
          </ul>
        </div>

        <a
          href="#signup"
          className="button"
          style={{
            marginTop: 28,
            justifyContent: "center",
            background: "var(--surface)",
            borderColor: "var(--border)",
            fontWeight: 700,
          }}
        >
          Scale Out
        </a>
      </div>
    </div>
  </section>
);

export const TestimonialsSection = () => (
  <section style={{ width: "100%", marginBottom: 90 }}>
    <div style={sectionHeaderStyle}>
      <div style={heroBadgeStyle}>Trusted in Production</div>
      <h2 style={sectionTitleStyle}>Loved by Builders & Agencies</h2>
      <p style={sectionSubtitleStyle}>
        See how automation teams rely on Supergreen for dependable client
        messaging.
      </p>
    </div>

    <div style={testimonialsGridStyle}>
      <div style={testimonialCardStyle}>
        <StarRating />
        <p
          style={{
            margin: 0,
            fontSize: "0.95em",
            lineHeight: 1.6,
            color: "var(--text)",
          }}
        >
          "We moved 25 client WhatsApp lines from Meta Cloud API to Supergreen.
          We eliminated over $1,100 in monthly per-conversation fees and finally
          unlocked full group chat triggers for client support."
        </p>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Alex Vance</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Founder, AutoFlow Agency
          </div>
        </div>
      </div>

      <div style={testimonialCardStyle}>
        <StarRating />
        <p
          style={{
            margin: 0,
            fontSize: "0.95em",
            lineHeight: 1.6,
            color: "var(--text)",
          }}
        >
          "The n8n community node is a game changer. I installed it directly in
          n8n, scanned the WhatsApp pairing code, and had dynamic PDF invoice
          delivery running in 5 minutes. No webhook headaches."
        </p>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Elena Rostova</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Senior Automation Engineer
          </div>
        </div>
      </div>

      <div style={testimonialCardStyle}>
        <StarRating />
        <p
          style={{
            margin: 0,
            fontSize: "0.95em",
            lineHeight: 1.6,
            color: "var(--text)",
          }}
        >
          "Every other headless WhatsApp service we tried got soft-blocked after
          2 or 3 days because of rotating IP churn. Supergreen's static proxy
          pinning keeps our numbers stable for months on end."
        </p>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Marcus Sterling</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            CTO, RetailBridge
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      q: "Why do WhatsApp numbers get banned, and how does Supergreen prevent it?",
      a: "Most bans on headless WhatsApp setups occur because of IP churn. When a server restarts or makes requests across different rotating proxy IPs, WhatsApp flags the session as hijacked and triggers a soft block. Supergreen guarantees that every account is pinned to a dedicated static residential proxy. Session credentials remain intact in isolated Docker volumes.",
    },
    {
      q: "How does Supergreen compare to Meta's Cloud API pricing?",
      a: "Meta charges between $0.03 and $0.08+ for every initiated conversation, requires strict pre-approval for every message template, and charges higher fees for marketing outreach. Supergreen charges a flat $10/month per line with zero per-message fees. You can send free-form text, media, PDFs, and polls without template approvals.",
    },
    {
      q: "Can I send messages to WhatsApp groups and read incoming group chats?",
      a: "Yes. Meta Cloud API strictly prohibits bots in WhatsApp groups. With Supergreen, your line functions as a regular participant with full permissions to read group messages, quote replies, mention participants, and trigger webhook automations.",
    },
    {
      q: "How do I install the official n8n node?",
      a: "If you are on n8n Cloud, search 'Supergreen' directly on the canvas and drag it into your workflow. On self-hosted instances, go to Settings → Community Nodes and enter 'n8n-nodes-supergreen'. Paste your account secret token, and you can send text, PDFs, polls, and media directly.",
    },
    {
      q: "What happens if a phone disconnects or loses battery?",
      a: "WhatsApp Web allows linked devices to remain active independently of the primary phone's battery state for weeks. If a network interruption occurs, our worker container runs an automated watchdog that transparently reconnects your session.",
    },
    {
      q: "Can I manage multiple phone numbers for different clients?",
      a: "Yes. Many automation agencies use Supergreen to manage dozens of client numbers. Each slot has its own dedicated Docker instance, static proxy, and independent webhook URL for clean multi-tenant isolation.",
    },
  ];

  return (
    <section id="faq" style={faqWrapperStyle}>
      <div style={sectionHeaderStyle}>
        <div style={heroBadgeStyle}>Got Questions?</div>
        <h2 style={sectionTitleStyle}>Frequently Asked Questions</h2>
        <p style={sectionSubtitleStyle}>
          Everything you need to know about bans, proxies, n8n, and scaling.
        </p>
      </div>

      {faqs.map((faq, idx) => {
        const isOpen = openIndex === idx;
        return (
          <div key={idx} style={faqItemStyle}>
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : idx)}
              style={faqQuestionStyle}
            >
              <span>{faq.q}</span>
              <span
                style={{
                  fontSize: 18,
                  color: isOpen ? "var(--emerald)" : "var(--text-muted)",
                  transform: isOpen ? "rotate(45deg)" : "none",
                  transition: "transform 0.15s ease",
                  lineHeight: 1,
                  fontWeight: 300,
                }}
              >
                +
              </span>
            </button>
            {isOpen && <div style={faqAnswerStyle}>{faq.a}</div>}
          </div>
        );
      })}
    </section>
  );
};

export const FinalCtaSection = () => (
  <section
    style={{ width: "100%", padding: "0 20px", boxSizing: "border-box" }}
  >
    <div style={finalCtaBoxStyle}>
      <div style={heroBadgeStyle}>Start in 60 Seconds</div>
      <h2
        style={{
          margin: 0,
          fontSize: "clamp(1.7rem, 4vw, 3rem)",
          fontWeight: 800,
          color: "var(--text)",
          letterSpacing: "-0.03em",
          lineHeight: 1.15,
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        Ready to Automate WhatsApp Without Meta's Tollbooth?
      </h2>
      <p
        style={{
          margin: 0,
          fontSize: "1.1em",
          color: "var(--text-muted)",
          maxWidth: 620,
          lineHeight: 1.6,
        }}
      >
        Connect your WhatsApp or Telegram line, install the n8n community node,
        and start dispatching unlimited messages today.
      </p>

      <div style={{ width: "100%", maxWidth: 440, marginTop: 10 }}>
        <a
          href="#signup"
          className="button primary"
          style={{
            width: "100%",
            height: 48,
            fontSize: 16,
            fontWeight: 700,
            background: "var(--emerald)",
            borderColor: "var(--emerald)",
            justifyContent: "center",
          }}
        >
          Claim Your Free Trial Line
        </a>
      </div>
    </div>
  </section>
);

export const FooterSection = () => (
  <footer style={footerStyle}>
    <div style={footerInnerStyle}>
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <GreenDotIcon size={18} />
          <span style={{ fontWeight: 800, fontSize: "1.1em" }}>Supergreen</span>
        </div>
        <p
          style={{
            color: "var(--text-muted)",
            fontSize: 13,
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          Flat-rate WhatsApp & Telegram API infrastructure for n8n, Make, and
          automation teams. Pinned static proxies and zero per-message fees.
        </p>
      </div>

      <div>
        <div style={footerColTitleStyle}>Product</div>
        <ul style={footerLinkListStyle}>
          <li>
            <a href="#n8n" style={footerLinkItemStyle}>
              n8n Community Node
            </a>
          </li>
          <li>
            <a href="#comparison" style={footerLinkItemStyle}>
              Meta API Comparison
            </a>
          </li>
          <li>
            <a href="#pricing" style={footerLinkItemStyle}>
              Agency Pricing
            </a>
          </li>
          <li>
            <a href="/docs" style={footerLinkItemStyle}>
              API Reference
            </a>
          </li>
          <li>
            <a href="/app" style={footerLinkItemStyle}>
              Developer Console
            </a>
          </li>
        </ul>
      </div>

      <div>
        <div style={footerColTitleStyle}>Ecosystem</div>
        <ul style={footerLinkListStyle}>
          <li>
            <a
              href="https://www.npmjs.com/package/n8n-nodes-supergreen"
              target="_blank"
              rel="noopener noreferrer"
              style={footerLinkItemStyle}
            >
              npm Package
            </a>
          </li>
          <li>
            <a
              href="https://github.com/uriva/n8n-nodes-supergreen"
              target="_blank"
              rel="noopener noreferrer"
              style={footerLinkItemStyle}
            >
              GitHub Community Node
            </a>
          </li>
          <li>
            <a
              href="https://supergreen.cc/llms.txt"
              target="_blank"
              rel="noopener noreferrer"
              style={footerLinkItemStyle}
            >
              llms.txt (AI Docs)
            </a>
          </li>
        </ul>
      </div>

      <div>
        <div style={footerColTitleStyle}>Legal & Support</div>
        <ul style={footerLinkListStyle}>
          <li>
            <a href="/terms" style={footerLinkItemStyle}>
              Terms of Service
            </a>
          </li>
          <li>
            <a href="/privacy" style={footerLinkItemStyle}>
              Privacy Policy
            </a>
          </li>
          <li>
            <a href="/refund-policy" style={footerLinkItemStyle}>
              Refund Policy
            </a>
          </li>
          <li>
            <a
              href={`mailto:${supportEmail}`}
              style={{ ...footerLinkItemStyle, color: "var(--emerald)" }}
            >
              {supportEmail}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div
      style={{
        maxWidth: 1060,
        margin: "0 auto",
        paddingTop: 20,
        borderTop: "1px solid var(--border-subtle)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        fontSize: 12,
        color: "var(--text-muted)",
      }}
    >
      <div>
        © {new Date().getFullYear()} Supergreen. All rights reserved.
      </div>
      <div>
        WhatsApp is a registered trademark of Meta Platforms, Inc. Supergreen is
        an independent software service.
      </div>
    </div>
  </footer>
);

export const AuthScreen = () => {
  const { isLoading, user } = useAuth();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "100vh",
        background: "var(--surface)",
        color: "var(--text)",
      }}
    >
      <HeaderNav user={user} />

      <main style={{ flex: 1 }}>
        {/* Hero Section */}
        <section style={heroContainerStyle}>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <div style={heroBadgeStyle}>
              <ZapIcon size={14} /> Flat-Rate WhatsApp & Telegram API
            </div>
            <a
              href="#n8n"
              style={{
                ...heroBadgeStyle,
                background: "var(--surface-card)",
                color: "var(--text-muted)",
                borderColor: "var(--border)",
                boxShadow: "none",
                textDecoration: "none",
              }}
            >
              <ZapIcon size={14} color="var(--emerald)" /> Verified on n8n Cloud
            </a>
          </div>

          <h1 style={heroTitleStyle}>
            Automate WhatsApp without{" "}
            <span style={heroGradientSpanStyle}>Meta's fees or bans.</span>
          </h1>

          <p style={heroSubtitleStyle}>
            Deploy headless sessions pinned to dedicated static residential
            proxies. Unlimited flat-rate messaging, real-time webhooks, full
            group chat support, and a native n8n community node.
          </p>

          <div id="signup" style={loginCardStyle}>
            <div style={{ fontSize: "1.1em", fontWeight: 700 }}>
              Access Developer Console
            </div>
            <AuthCardContent isLoading={isLoading} user={user} />
          </div>

          <div style={heroPillsStyle}>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <CheckIcon size={13} color="var(--emerald)" strokeWidth={2.5} />
              Flat ${monthlyPaymentPerLine}/month slot
            </span>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <CheckIcon size={13} color="var(--emerald)" strokeWidth={2.5} />
              Zero per-message markup
            </span>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <CheckIcon size={13} color="var(--emerald)" strokeWidth={2.5} />
              Dedicated Docker isolation
            </span>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <CheckIcon size={13} color="var(--emerald)" strokeWidth={2.5} />
              Static proxy pinning
            </span>
          </div>
        </section>

        {/* 4 Massive Stat Counters */}
        <section style={statsGridStyle}>
          <div style={statCardStyle}>
            <div style={statNumberStyle}>$0.00</div>
            <div style={statLabelStyle}>
              Per outbound message compared to Meta's $0.05+ conversation fees
            </div>
          </div>

          <div style={statCardStyle}>
            <div style={statNumberStyle}>99.9%</div>
            <div style={statLabelStyle}>
              Session uptime with dedicated static residential proxy pinning
            </div>
          </div>

          <div style={statCardStyle}>
            <div style={statNumberStyle}>
              n8n Cloud
            </div>
            <div style={statLabelStyle}>
              Verified community node ready directly on your n8n canvas
            </div>
          </div>

          <div style={statCardStyle}>
            <div style={statNumberStyle}>100%</div>
            <div style={statLabelStyle}>
              WhatsApp group chat read and write support for your workflows
            </div>
          </div>
        </section>

        {/* Interactive Code & n8n Showcase */}
        <InteractiveShowcase />

        {/* Bento Core Benefits Grid */}
        <BentoFeatures />

        {/* Supergreen vs Meta Comparison */}
        <ComparisonSection />

        {/* Customer Testimonials */}
        <TestimonialsSection />

        {/* Agency & Solo Pricing */}
        <AgencyPricingSection />

        {/* Comprehensive FAQ Accordion */}
        <FaqSection />

        {/* Final Dramatic Hero CTA */}
        <FinalCtaSection />
      </main>

      <FooterSection />
    </div>
  );
};

export { AuthScreen as Login };
